import { Controller } from "@hotwired/stimulus"

// Visible save indicator + conflict banner for the project editor.
//
// Listens for events emitted by `autosave_controller`:
//   - autosave:pending  → "Изменено…" (debounce window)
//   - autosave:saving   → "Сохраняется…"
//   - autosave:saved    → "Сохранено HH:MM" (hides after 3 s, then "Синхронизировано")
//   - autosave:offline  → "Нет связи — повторим"  (red-ish)
//   - autosave:conflict → sticky top banner with Update / Overwrite buttons
//   - autosave:error    → persistent error pill ("Сохранение недоступно")
//
// Only mounts when the root element has `data-project-id` (server mode).
export default class extends Controller {
  static targets = ["pill", "label", "banner", "bannerMessage"]

  connect() {
    if (!this.element.dataset.projectId) return // anon /editor — no indicator
    this._visible = true
    this.#renderIdle()

    this._onPending  = () => this.#renderPending()
    this._onSaving   = () => this.#renderSaving()
    this._onSaved    = (e) => this.#renderSaved(e.detail?.at || new Date())
    this._onOffline  = () => this.#renderOffline()
    this._onConflict = (e) => this.#renderConflict(e.detail)
    this._onError    = (e) => this.#renderError(e.detail)

    this.element.addEventListener("autosave:pending",  this._onPending)
    this.element.addEventListener("autosave:saving",   this._onSaving)
    this.element.addEventListener("autosave:saved",    this._onSaved)
    this.element.addEventListener("autosave:offline",  this._onOffline)
    this.element.addEventListener("autosave:conflict", this._onConflict)
    this.element.addEventListener("autosave:error",    this._onError)
  }

  disconnect() {
    this.element.removeEventListener("autosave:pending",  this._onPending)
    this.element.removeEventListener("autosave:saving",   this._onSaving)
    this.element.removeEventListener("autosave:saved",    this._onSaved)
    this.element.removeEventListener("autosave:offline",  this._onOffline)
    this.element.removeEventListener("autosave:conflict", this._onConflict)
    this.element.removeEventListener("autosave:error",    this._onError)
    if (this._savedTimer) clearTimeout(this._savedTimer)
  }

  #setPill({ text, tone }) {
    if (!this.hasPillTarget || !this.hasLabelTarget) return
    const tones = {
      idle:     "bg-neutral-100 text-neutral-600 border-neutral-200",
      pending:  "bg-amber-50  text-amber-700   border-amber-200",
      saving:   "bg-indigo-50 text-indigo-700  border-indigo-200",
      saved:    "bg-emerald-50 text-emerald-700 border-emerald-200",
      offline:  "bg-rose-50  text-rose-700    border-rose-200",
      error:    "bg-rose-50  text-rose-800    border-rose-300",
    }
    this.labelTarget.textContent = text
    // Reset tone classes then apply current. classList.remove/add throw on
    // empty strings, so split-then-filter to be safe against stray spaces.
    const splitCls = (s) => s.split(/\s+/).filter(Boolean)
    Object.values(tones).forEach(cls => splitCls(cls).forEach(c => this.pillTarget.classList.remove(c)))
    splitCls(tones[tone]).forEach(c => this.pillTarget.classList.add(c))
  }

  #renderIdle()    { this.#setPill({ text: "Синхронизировано", tone: "idle" }) }
  #renderPending() { this.#setPill({ text: "Изменено…",         tone: "pending" }) }
  #renderSaving()  { this.#setPill({ text: "Сохраняется…",      tone: "saving" }) }
  #renderOffline() { this.#setPill({ text: "Нет связи — повторим", tone: "offline" }) }
  #renderError({ status }) {
    this.#setPill({ text: `Сохранение недоступно (${status ?? "?"})`, tone: "error" })
  }

  #renderSaved(at) {
    const hhmm = at.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })
    this.#setPill({ text: `Сохранено ${hhmm}`, tone: "saved" })
    if (this._savedTimer) clearTimeout(this._savedTimer)
    this._savedTimer = setTimeout(() => this.#renderIdle(), 3000)
  }

  #renderConflict(detail) {
    this.#setPill({ text: "Конфликт", tone: "error" })
    this._conflictDetail = detail
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.remove("hidden")
      if (this.hasBannerMessageTarget) {
        const when = new Date(detail.updatedAt).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })
        this.bannerMessageTarget.textContent = `Проект был изменён в другой вкладке в ${when}. Выберите, что сохранить.`
      }
    }
  }

  // Button handlers below assume `autosave` controller lives on the SAME
  // DOM element as `save-status` (both listed in `data-controller` on the
  // editor root). If someone moves `save-status` to a child element in the
  // future, `getControllerForElementAndIdentifier(this.element, ...)` will
  // return null and the Update/Overwrite buttons will silently do nothing.

  // Button handler: GET the fresh project state and push it into the store.
  async bannerUpdate() {
    const detail = this._conflictDetail
    if (!detail) return
    // editor_controller's hydrate path accepts the same shape.
    const { editorStore } = await import("../editor/store")
    editorStore.restoreDraft({
      plot: detail.state?.plot || null,
      objects: Array.isArray(detail.state?.objects) ? detail.state.objects : [],
      norms: detail.state?.norms,
      ui: detail.state?.ui,
    })
    // Tell autosave the lock_version we're now on so the next change saves cleanly.
    const autosaveEl = this.element
    const autosaveCtrl = this.application.getControllerForElementAndIdentifier(autosaveEl, "autosave")
    if (autosaveCtrl) autosaveCtrl._lockVersion = detail.lockVersion
    if (autosaveCtrl) autosaveCtrl._fatal = false
    if (this.hasBannerTarget) this.bannerTarget.classList.add("hidden")
    this.#renderIdle()
  }

  // Button handler: push our current state over the server's.
  bannerOverwrite() {
    const detail = this._conflictDetail
    if (!detail) return
    const autosaveCtrl = this.application.getControllerForElementAndIdentifier(this.element, "autosave")
    if (autosaveCtrl) autosaveCtrl.forceOverwrite(detail.lockVersion)
    if (this.hasBannerTarget) this.bannerTarget.classList.add("hidden")
  }
}
